import {
  DocumentData,
  DocumentSnapshot,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { FaSpinner, FaToilet } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { db, requestNotificationPermission } from "../firebase";
import { Toilet } from "../types";

// הוספת טיפוס עזר
type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

// המרת האייקונים לקומפוננטות React
const SpinnerIcon = FaSpinner as unknown as IconComponent;
const ToiletIcon = FaToilet as unknown as IconComponent;

interface User {
  id: string;
  name: string;
  gender: "male" | "female";
}

interface QueueItem {
  id: string;
  name: string;
  timestamp: number;
  gender: "male" | "female";
  userId: string;
}

export const ToiletStatusComponent: React.FC = () => {
  const [toilet, setToilet] = useState<Toilet | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [users, setUsers] = useState<User[]>([]);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isOccupied, setIsOccupied] = useState<boolean>(false);
  const [userGender, setUserGender] = useState<"male" | "female" | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userName, setUserName] = useState<string>("");
  const [isJoiningQueue, setIsJoiningQueue] = useState(false);
  const [isLeavingQueue, setIsLeavingQueue] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isEnteringToilet, setIsEnteringToilet] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const savedUserId = localStorage.getItem("selectedUserId");
    if (!savedUserId) {
      navigate("/");
      return;
    }
    setSelectedUserId(savedUserId);

    // Request notification permission when component mounts
    const setupNotifications = async () => {
      try {
        const token = await requestNotificationPermission();
        if (token) {
          console.log("Notification token:", token);
          // Store the token in Firestore for the current user
          const userId = localStorage.getItem("selectedUserId");
          if (userId) {
            await updateDoc(doc(db, "users", userId), {
              notificationToken: token,
            });
          }
        }
      } catch (error) {
        console.error("Error setting up notifications:", error);
      }
    };

    setupNotifications();

    const fetchUsers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        const usersData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as User[];
        setUsers(usersData);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();

    const fetchUserGender = async () => {
      try {
        const userDoc = await getDocs(collection(db, "users"));
        const user = userDoc.docs.find((doc) => doc.id === savedUserId);
        if (user) {
          setUserGender(user.data().gender);
          setUserName(user.data().name);
        }
      } catch (error) {
        console.error("Error fetching user gender:", error);
      }
    };

    fetchUserGender();

    const fetchQueue = async () => {
      try {
        const queueSnapshot = await getDocs(collection(db, "queue"));
        const queueData = queueSnapshot.docs
          .map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              } as QueueItem)
          )
          .filter((item) => item.gender === userGender)
          .sort((a, b) => a.timestamp - b.timestamp);

        setQueue(queueData);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching queue:", error);
        setIsLoading(false);
      }
    };

    const fetchToiletStatus = async () => {
      try {
        if (!userGender) return;

        const statusDoc = await getDoc(doc(db, "toiletStatus", userGender));
        if (statusDoc.exists()) {
          const statusData = statusDoc.data();
          const wasOccupied = isOccupied;
          const newStatus = statusData.isOccupied;
          setIsOccupied(newStatus);

          // אם השירותים התפנו ויש אנשים בתור, ה-Cloud Function תתעורר אוטומטית
          if (wasOccupied && !newStatus && queue.length > 0) {
            console.log(
              "Toilet became available, Cloud Function will handle notification"
            );
          }
        }
      } catch (error) {
        console.error("Error fetching toilet status:", error);
      }
    };

    fetchQueue();
    fetchToiletStatus();

    const interval = setInterval(() => {
      fetchQueue();
      fetchToiletStatus();
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [navigate, userGender, isOccupied, queue.length]);

  useEffect(() => {
    if (!selectedUserId) return;

    const userDoc = doc(db, "users", selectedUserId);
    const unsubscribe = onSnapshot(
      userDoc,
      (docSnapshot: DocumentSnapshot<DocumentData>) => {
        const userData = docSnapshot.data();
        if (userData) {
          const toiletDoc = doc(db, "toilets", userData.gender);
          onSnapshot(
            toiletDoc,
            (toiletSnapshot: DocumentSnapshot<DocumentData>) => {
              const toiletData = toiletSnapshot.data() as Toilet;
              setToilet(toiletData);
            }
          );
        }
      }
    );

    return () => unsubscribe();
  }, [selectedUserId]);

  const handleStatusChange = async (newStatus: boolean) => {
    if (!userGender) return;
    setIsUpdatingStatus(true);

    try {
      const statusRef = doc(db, "toiletStatus", userGender);
      await setDoc(
        statusRef,
        {
          isOccupied: newStatus,
          lastUpdated: new Date().toISOString(),
        },
        { merge: true }
      );
      setIsOccupied(newStatus);
    } catch (error) {
      console.error("Error updating toilet status:", error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleJoinQueue = async () => {
    if (!selectedUserId || !userGender || isJoiningQueue) return;
    setIsJoiningQueue(true);

    // עדכון מיידי של הממשק
    const newQueueItem: QueueItem = {
      id: "temp-" + Date.now(), // מזהה זמני
      userId: selectedUserId,
      name: userName,
      gender: userGender,
      timestamp: Date.now(),
    };
    setQueue((prev) => [...prev, newQueueItem]);

    try {
      // עדכון השרת ברקע
      const docRef = await addDoc(collection(db, "queue"), {
        userId: selectedUserId,
        name: userName,
        gender: userGender,
        timestamp: serverTimestamp(),
      });

      // עדכון המזהה הזמני למזהה האמיתי מהשרת
      setQueue((prev) =>
        prev.map((item) =>
          item.id === newQueueItem.id ? { ...item, id: docRef.id } : item
        )
      );
    } catch (error) {
      console.error("Error joining queue:", error);
      // אם יש שגיאה, נחזיר את המצב הקודם
      setQueue((prev) => prev.filter((item) => item.id !== newQueueItem.id));
    } finally {
      setIsJoiningQueue(false);
    }
  };

  const handleLeaveQueue = async () => {
    if (!selectedUserId || isLeavingQueue) return;
    setIsLeavingQueue(true);

    // שמירת התור הנוכחי למקרה שנצטרך להחזיר אותו
    const currentQueue = [...queue];

    // עדכון מיידי של הממשק
    setQueue((prev) => prev.filter((item) => item.userId !== selectedUserId));

    try {
      const queueRef = collection(db, "queue");
      const q = query(queueRef, where("userId", "==", selectedUserId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userQueueDoc = querySnapshot.docs[0];
        await deleteDoc(userQueueDoc.ref);
      }
    } catch (error) {
      console.error("Error leaving queue:", error);
      // אם יש שגיאה, נחזיר את המצב הקודם
      setQueue(currentQueue);
    } finally {
      setIsLeavingQueue(false);
    }
  };

  const isInQueue = queue.some((item) => item.userId === selectedUserId);
  const userPosition = isInQueue
    ? queue.findIndex((item) => item.userId === selectedUserId) + 1
    : 0;

  const handleEnterToilet = async () => {
    if (!selectedUserId || !toilet || isEnteringToilet) return;
    setIsEnteringToilet(true);

    // עדכון מיידי של הממשק
    const currentToilet = { ...toilet };
    setToilet((prev) =>
      prev
        ? {
            ...prev,
            status: "occupied",
            currentUser: selectedUserId,
            occupiedBy: selectedUserId,
            occupiedAt: new Date(),
            isOccupied: true,
            lastStatusUpdate: new Date(),
          }
        : null
    );

    try {
      // עדכון השרת ברקע
      await updateDoc(doc(db, "toilets", toilet.id), {
        status: "occupied",
        currentUser: selectedUserId,
        occupiedBy: selectedUserId,
        occupiedAt: new Date(),
        isOccupied: true,
        lastStatusUpdate: new Date(),
      });

      // הסרת המשתמש מהתור
      const queueRef = collection(db, "queue");
      const q = query(queueRef, where("userId", "==", selectedUserId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userQueueDoc = querySnapshot.docs[0];
        await deleteDoc(userQueueDoc.ref);
        setQueue((prev) =>
          prev.filter((item) => item.userId !== selectedUserId)
        );
      }
      handleStatusChange(true);
    } catch (error) {
      console.error("Error entering toilet:", error);
      // אם יש שגיאה, נחזיר את המצב הקודם
      setToilet(currentToilet);
    } finally {
      setIsEnteringToilet(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!toilet) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-center">סטטוס השירותים</h1>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold mb-4 text-center">
            תור לשירותים - {userGender === "male" ? "גברים" : "נשים"}
          </h2>

          {!isInQueue ? (
            <button
              onClick={handleJoinQueue}
              disabled={isJoiningQueue}
              className="w-full py-3 px-4 bg-blue-500 text-white rounded-lg shadow-md hover:bg-blue-600 transition-colors mb-6 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isJoiningQueue ? (
                <>
                  <SpinnerIcon className="animate-spin mr-2" />
                  מצטרף...
                </>
              ) : (
                <>
                  הצטרף לתור
                  {queue.length > 0 && (
                    <span className="mr-2">({queue.length} ממתינים)</span>
                  )}
                </>
              )}
            </button>
          ) : (
            <div className="text-center text-xl mb-6">
              אתה {userPosition} מתוך {queue.length} ממתינים
            </div>
          )}

          <div className="mt-6">
            <h3 className="text-xl font-semibold mb-4 text-center">
              מצב השירותים
            </h3>
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div
                className={`text-4xl ${
                  isOccupied ? "text-red-500" : "text-green-500"
                }`}
              >
                <ToiletIcon />
              </div>
              <div className="text-xl font-semibold">
                {isOccupied ? "תפוס" : "פנוי"}
              </div>
            </div>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => handleStatusChange(true)}
                disabled={isUpdatingStatus || isOccupied}
                className={`px-4 py-2 rounded-lg ${
                  isOccupied
                    ? "bg-red-500 text-white"
                    : "bg-gray-200 text-gray-700"
                } disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
              >
                {isUpdatingStatus ? (
                  <>
                    <SpinnerIcon className="animate-spin mr-2" />
                    מעדכן...
                  </>
                ) : (
                  "סמן כתפוס"
                )}
              </button>
              <button
                onClick={() => handleStatusChange(false)}
                disabled={isUpdatingStatus || !isOccupied}
                className={`px-4 py-2 rounded-lg ${
                  !isOccupied
                    ? "bg-green-500 text-white"
                    : "bg-gray-200 text-gray-700"
                } disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
              >
                {isUpdatingStatus ? (
                  <>
                    <SpinnerIcon className="animate-spin mr-2" />
                    מעדכן...
                  </>
                ) : (
                  "סמן כפנוי"
                )}
              </button>
            </div>
          </div>

          {isInQueue && (
            <div className="flex justify-center space-x-4 mt-6">
              <button
                onClick={handleLeaveQueue}
                disabled={isLeavingQueue}
                className="px-4 py-2 rounded-lg bg-red-500 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLeavingQueue ? (
                  <>
                    <SpinnerIcon className="animate-spin mr-2" />
                    עוזב...
                  </>
                ) : (
                  "עזוב תור"
                )}
              </button>
              {!isOccupied && userPosition === 1 && (
                <button
                  onClick={handleEnterToilet}
                  disabled={isEnteringToilet}
                  className="px-4 py-2 rounded-lg bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isEnteringToilet ? (
                    <>
                      <SpinnerIcon className="animate-spin mr-2" />
                      נכנס...
                    </>
                  ) : (
                    "נכנסתי"
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToiletStatusComponent;
